import React, { useEffect, useMemo, useState } from 'react';

import { AuthThirdPartyCookiesMessage } from '@/components/auth-third-party-cookies-message';
import { RETURN_TO_KEY } from '@/components/auth0-provider';
import { useMixpanelIdentify } from '@/hooks/use-mixpanel-identify';
import { makeApolloClient } from '@/lib/apollo-client';
import { isOAuthSilentAuthLoginRequired } from '@/lib/utils/auth0-oauth-error';
import { ApolloProvider as RootApolloProvider } from '@apollo/client';
import { useAuth0 } from '@auth0/auth0-react';
import { AlertCircle, Loader2 } from 'lucide-react';

import { Login } from './login';
import { Button } from './ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from './ui/card';

export function withAuth<T extends object>(
  WrappedComponent: React.ComponentType<T>
) {
  const AuthenticatedComponent = (props: T) => {
    const {
      isLoading,
      error,
      isAuthenticated,
      getAccessTokenSilently,
      loginWithRedirect,
    } = useAuth0();

    const [authToken, setAuthToken] = useState<string | null>(null);
    const [tokenError, setTokenError] = useState<string | null>(null);
    const [oauthSilentFailure, setOauthSilentFailure] = useState(false);

    useMixpanelIdentify();

    useEffect(() => {
      let isMounted = true;
      if (isAuthenticated) {
        getAccessTokenSilently()
          .then((token) => {
            if (isMounted && token) {
              setAuthToken(token);
              setTokenError(null);
              setOauthSilentFailure(false);
            }
          })
          .catch((err) => {
            console.error('Error getting access token:', err);
            if (isMounted) {
              if (isOAuthSilentAuthLoginRequired(err)) {
                setOauthSilentFailure(true);
                setTokenError(null);
              } else {
                setOauthSilentFailure(false);
                setTokenError(
                  err instanceof Error
                    ? err.message
                    : 'Failed to get access token'
                );
              }
            }
          });
      }
      return () => {
        isMounted = false;
      };
    }, [getAccessTokenSilently, isAuthenticated]);

    const apolloClient = useMemo(
      () =>
        authToken
          ? makeApolloClient(authToken, () => getAccessTokenSilently())
          : null,
      [authToken, getAccessTokenSilently]
    );

    // Loading state while Auth0 initializes
    if (isLoading) {
      return (
        <div className="flex h-screen flex-col items-center justify-center gap-4">
          <Loader2 className="h-6 w-6 animate-spin text-primary" />
        </div>
      );
    }

    if (oauthSilentFailure) {
      return <AuthThirdPartyCookiesMessage />;
    }

    // Auth0 authentication error
    if (error) {
      return (
        <div className="flex min-h-screen items-center justify-center bg-gradient-to-br from-background to-muted p-4">
          <Card className="w-full max-w-md">
            <CardHeader className="space-y-4 text-center">
              <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-destructive/10">
                <AlertCircle className="h-8 w-8 text-destructive" />
              </div>
              <div>
                <CardTitle className="text-2xl">Authentication Error</CardTitle>
                <CardDescription className="mt-2 text-base">
                  {error.message}
                </CardDescription>
              </div>
            </CardHeader>
            <CardContent>
              <Button
                onClick={() => loginWithRedirect()}
                className="w-full"
                variant="default"
              >
                Try Again
              </Button>
            </CardContent>
          </Card>
        </div>
      );
    }

    // Not authenticated - show login
    if (!isAuthenticated) {
      // Store the current URL before showing login (in case Login component doesn't capture it)
      if (typeof window !== 'undefined') {
        const currentPath =
          window.location.pathname +
          window.location.search +
          window.location.hash;
        if (
          !['/login', '/redirect', '/logout', '/'].includes(
            window.location.pathname
          )
        ) {
          localStorage.setItem(RETURN_TO_KEY, currentPath);
        }
      }
      return <Login />;
    }

    // Token error
    if (tokenError) {
      const handleForceLogout = () => {
        // Clear all Auth0 cached data
        localStorage.clear();
        sessionStorage.clear();

        // Force logout and redirect back to login
        window.location.href = '/logout';
      };

      return (
        <div className="flex min-h-screen items-center justify-center bg-gradient-to-br from-background to-muted p-4">
          <Card className="w-full max-w-md">
            <CardHeader className="space-y-4 text-center">
              <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-destructive/10">
                <AlertCircle className="h-8 w-8 text-destructive" />
              </div>
              <div>
                <CardTitle className="text-2xl">Token Error</CardTitle>
                <CardDescription className="mt-2 text-base">
                  {tokenError}
                </CardDescription>
              </div>
            </CardHeader>
            <CardContent className="space-y-2">
              <Button
                onClick={handleForceLogout}
                className="w-full"
                variant="default"
              >
                Logout and Try Again
              </Button>
              <p className="text-center text-xs text-muted-foreground">
                This will clear your session and log you out completely
              </p>
            </CardContent>
          </Card>
        </div>
      );
    }

    // Loading token
    if (!authToken) {
      return (
        <div className="flex h-screen flex-col items-center justify-center gap-4">
          <Loader2 className="h-12 w-12 animate-spin text-primary" />
          <p className="text-sm text-muted-foreground">
            Getting access token...
          </p>
        </div>
      );
    }

    // Authenticated with token - render the wrapped component
    return (
      <RootApolloProvider client={apolloClient!}>
        <WrappedComponent {...props} />
      </RootApolloProvider>
    );
  };

  AuthenticatedComponent.displayName = `withAuth(${
    WrappedComponent.displayName || WrappedComponent.name || 'Component'
  })`;

  return AuthenticatedComponent;
}
